import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || "Gagal masuk.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1585677252170-18d09c51269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-900/80 to-emerald-900/40" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500">
            <Store className="h-6 w-6" />
          </div>
          <span className="font-heading text-xl font-bold">KalkuBisnis UMKM</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Hitung HPP, Harga Jual & Untung dalam hitungan detik.
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Kalkulator biaya, titik impas (BEP), dan kasir praktis untuk pemilik usaha kecil — tanpa perlu latar belakang akuntansi.
          </p>
        </div>
        <p className="relative text-sm text-slate-400">Dipercaya oleh UMKM kuliner, jasa, & kerajinan.</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="mb-3 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <Store className="h-5 w-5" />
              </div>
              <span className="font-heading text-lg font-bold">KalkuBisnis UMKM</span>
            </div>
          </div>
          <h2 className="font-heading text-2xl font-bold">Masuk ke KalkuBisnis</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kalkulator HPP, Harga Jual, BEP & Kasir untuk UMKM Indonesia.
          </p>

          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com" required data-testid="login-email-input"
                className="rounded-xl py-2.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <PasswordInput
                id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password-input"
                className="rounded-xl py-2.5"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full rounded-xl py-2.5 min-h-[44px]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
